import type { FC } from 'react'
import React, { memo, useMemo } from 'react'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import { ResponsiveBar } from '@nivo/bar'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { Box } from '@material-ui/core'
import { isEqual } from '@homeviews/utils'
import CustomBar from '../CustomBar'
import NoData from '../NoData'
import ScreenshotButton from '../ScreenshotButton'

import { isEngageUser, useGetReviewsStatsQuery } from '@homeviews/store'
import { chartTheme } from '../../helpers/layout'
import { Spinner, Tooltip, Typography } from '@homeviews/shared-ui'
import {
  SUSTAINABILITY_LABELS_MAP,
  MD_SUSTAINABILITY_LABELS_MAP,
} from '../../helpers/settings'
import { transformSustainabilityChartData } from '../../helpers/transform'
import { getTspanGroups } from '../../helpers/getTspanGroups'
import { useSelector } from 'react-redux'

const useStyles = makeStyles(() =>
  createStyles({
    root: {},
    title: {
      marginBottom: 16,
    },
  }),
)

const format = (v) => `${v}%`

interface IProps {
  activeFilters: any
  graphDisabled
  gridRef?: HTMLElement
  filtersDetails: any
}

const SustainabilityChart: FC<IProps> = memo(
  ({ activeFilters, graphDisabled, gridRef, filtersDetails }) => {
    const theme = useTheme()
    const classes = useStyles()

    const isMobile = useMediaQuery('(max-width:1580px)')

    const isEngagePackage = Boolean(useSelector(isEngageUser))

    const {
      data: reviewsSustainability,
      isFetching: isFetchingReviewsSustainability,
    } = useGetReviewsStatsQuery(
      {
        aggregate_fieldset: 'sustainability',
        aggregate: 'avg',
        ...activeFilters,
        engage: isEngagePackage,
      },
      {
        skip: graphDisabled,
      },
    )

    const reviewsSustainabilityData = useMemo(
      () => transformSustainabilityChartData(reviewsSustainability?.data),
      [reviewsSustainability?.data],
    )

    const chartData = reviewsSustainabilityData

    if (!chartData || !chartData?.length) {
      if (gridRef) {
        gridRef.style.display = 'none'
      }
      return <NoData />
    } else {
      if (gridRef) {
        gridRef.style.display = 'block'
      }
    }

    if (isFetchingReviewsSustainability) {
      return <Spinner centered />
    }

    return (
      <>
        <div className="h-exportableDataTitle">
          <Typography variant="h3" className={classes.title}>
            Which environmentally sustainable features are most important to
            your residents?
          </Typography>
          <ScreenshotButton />
          <Tooltip
            title=""
            text="Reviewers can select up to 3 features that are most important to them."
          />
        </div>
        {filtersDetails && filtersDetails}
        <div className="l-chartContainer">
          <div className="l-chartWrapper">
            <ResponsiveBar
              data={chartData}
              barComponent={(barProps) => (
                <CustomBar {...barProps} shouldRenderLabel />
              )}
              keys={['value']}
              indexBy="id"
              theme={chartTheme}
              animate={false}
              isInteractive={false}
              enableGridX={false}
              axisTop={null}
              axisRight={null}
              borderRadius={5}
              maxValue={100}
              axisLeft={{
                tickSize: 0,
                format,
              }}
              padding={0.3}
              margin={{
                top: 50,
                right: 20,
                bottom: isMobile ? 80 : 60,
                left: 30,
              }}
              colors={() => {
                return '#00A193'
              }}
              label={(labelValue) => {
                return (
                  <tspan y={-12}>
                    {Number(labelValue.formattedValue).toFixed(2)}%
                  </tspan>
                ) as unknown as string
              }}
              labelTextColor="#4E516B"
              axisBottom={{
                tickSize: 0,
                tickPadding: 25,
                tickRotation: 0,
                renderTick: ({
                  opacity,
                  textAnchor,
                  textX,
                  textY,
                  value,
                  x,
                  y,
                }) => {
                  return (
                    <g transform={`translate(${x},${y})`} style={{ opacity }}>
                      <text
                        textAnchor={textAnchor}
                        transform={`translate(${textX},${textY})`}
                        style={{
                          fontFamily: '"Effra", sans-serif',
                          fontSize: '10px',
                          fill: 'rgb(130, 139, 151)',
                        }}
                      >
                        {getTspanGroups(
                          value,
                          isMobile
                            ? MD_SUSTAINABILITY_LABELS_MAP
                            : SUSTAINABILITY_LABELS_MAP,
                          theme,
                        )}
                      </text>
                    </g>
                  )
                },
              }}
            />
          </div>
        </div>
        <Typography>Data collected from February 2021</Typography>
        <Box marginTop={1}>
          <Typography>
            Reviewers can select up to 3 features that are most important to
            them.
          </Typography>
        </Box>
      </>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default SustainabilityChart
